import { useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, UserPlus } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { isGenericInbox, GENERIC_EMAIL_MESSAGE } from "@/lib/genericEmail";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { DuplicateContactError, throwContactWriteError, toastDuplicateContact } from "@/lib/duplicateContact";

interface AddContactModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preselectedCompanyId?: string;
  preselectedCompanyName?: string;
}

export function AddContactModal({
  open,
  onOpenChange,
  preselectedCompanyId,
  preselectedCompanyName,
}: AddContactModalProps) {
  const formId = useId();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [title, setTitle] = useState("");
  const [linkedIn, setLinkedIn] = useState("");
  const [isPrimary, setIsPrimary] = useState(false);
  const [isCoi, setIsCoi] = useState(false);
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();

  const createContactMutation = useMutation({
    mutationFn: async (data: {
      name: string;
      email?: string;
      phone?: string;
      title?: string;
      linkedinUrl?: string;
      isPrimary: boolean;
      companyId?: string;
      markAsCoi?: boolean;
    }) => {
      const res = await fetch("/api/contacts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        await throwContactWriteError(res, "Failed to create contact");
      }
      return res.json();
    },
    onSuccess: (_created, vars) => {
      toast.success(vars.markAsCoi ? "Contact added as a COI" : "Contact added");
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      if (vars.markAsCoi) for (const key of [["cois"], ["coi-bd"], ["coi-firm"]]) queryClient.invalidateQueries({ queryKey: key });
      if (preselectedCompanyId) {
        queryClient.invalidateQueries({ queryKey: ["company", preselectedCompanyId] });
      }
      resetDraft();
      onOpenChange(false);
    },
    onError: (error: Error) => {
      if (error instanceof DuplicateContactError) return toastDuplicateContact(error.body, navigate);
      toast.error(error.message);
    },
  });

  const handleClose = () => { if (!createContactMutation.isPending) onOpenChange(false); };
  const resetDraft = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setTitle("");
    setLinkedIn("");
    setIsPrimary(false);
    setIsCoi(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (createContactMutation.isPending) return;
    if (!firstName.trim() || !lastName.trim()) {
      toast.error("First and last name are required");
      return;
    }

    createContactMutation.mutate({
      name: `${firstName.trim()} ${lastName.trim()}`,
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      title: title.trim() || undefined,
      linkedinUrl: linkedIn.trim() || undefined,
      isPrimary,
      companyId: preselectedCompanyId,
      markAsCoi: isCoi,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-[480px] flex flex-col overflow-hidden p-0">
        <DialogHeader className="px-6 pt-6 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-primary-text" />
            Add Contact
          </DialogTitle>
          <DialogDescription className="sr-only">Enter contact details. Required fields are marked with an asterisk.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col min-h-0">
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <fieldset disabled={createContactMutation.isPending} className="min-w-0 px-6 py-4 space-y-4">
          {preselectedCompanyName && (
            <div className="px-3 py-2 bg-muted/50 rounded-lg text-sm">
              Adding to <span className="font-medium">{preselectedCompanyName}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor={`${formId}-firstName`} className="text-xs">First Name <span className="text-red-500">*</span></Label>
              <Input id={`${formId}-firstName`} required value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="John" className="h-9" autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`${formId}-lastName`} className="text-xs">Last Name <span className="text-red-500">*</span></Label>
              <Input id={`${formId}-lastName`} required value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Smith" className="h-9" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`${formId}-email`} className="text-xs">Email</Label>
            <Input type="email" id={`${formId}-email`} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="john@company.com"
              className={cn("h-9", isGenericInbox(email.trim()) && "border-destructive focus-visible:ring-destructive")} />
            {isGenericInbox(email.trim()) && (
              <p className="text-xs text-destructive leading-snug">{GENERIC_EMAIL_MESSAGE}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor={`${formId}-phone`} className="text-xs">Phone</Label>
              <Input id={`${formId}-phone`} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" className="h-9" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`${formId}-title`} className="text-xs">Title</Label>
              <Input id={`${formId}-title`} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="CEO" className="h-9" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`${formId}-linkedIn`} className="text-xs">LinkedIn URL</Label>
            <Input id={`${formId}-linkedIn`} value={linkedIn} onChange={(e) => setLinkedIn(e.target.value)} placeholder="linkedin.com/in/johnsmith" className="h-9" />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox id={`${formId}-isPrimary`} checked={isPrimary} onCheckedChange={(checked) => setIsPrimary(checked as boolean)} />
            <Label htmlFor={`${formId}-isPrimary`} className="text-sm font-normal">Primary contact</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox id={`${formId}-isCoi`} checked={isCoi} onCheckedChange={(checked) => setIsCoi(checked as boolean)} />
            <Label htmlFor={`${formId}-isCoi`} className="text-sm font-normal">Center of influence <span className="text-muted-foreground">(sends us owners; gets the COI view)</span></Label>
          </div>

          </fieldset>
          </div>
          {createContactMutation.error && <p role="alert" className="px-6 text-sm text-destructive">{createContactMutation.error.message}</p>}
          <p className="shrink-0 px-6 pb-2 text-xs text-muted-foreground">Closing keeps your draft until you leave this page.</p>
          <div className="flex flex-wrap gap-2 justify-end px-6 py-4 border-t shrink-0">
            <Button type="button" variant="ghost" disabled={createContactMutation.isPending} onClick={() => { resetDraft(); handleClose(); }}>Discard draft</Button>
            <Button type="button" variant="ghost" disabled={createContactMutation.isPending} onClick={handleClose}>Close</Button>
            <Button type="submit" disabled={createContactMutation.isPending || !firstName.trim() || !lastName.trim() || isGenericInbox(email.trim())} className="gap-1.5">
              {createContactMutation.isPending ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving...</> : "Add Contact"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
